import React, { useState, useEffect, useCallback } from "react";
import { CircularProgress } from "@mui/material";
import { Line } from "react-chartjs-2";
import { getPaycentralTransactionsChartDataAction } from "../../Actions/PaycentralAction";
import { getTransactionsChartDataAction } from "../../Actions/TransactionActions";
import NoDataFound from "./NoDataFound";
import { MyCancelIcon } from "../../Assets/svg/MyIcons";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const DashboardTransactionGraph = ({
  selectedMerchant,
  selectedWebsite,
  selectedTimeRange,
  startDate,
  endDate,
  showButton,
  onRemove,
  onShowSnackbar,
  tenantType
}) => {
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: []
  });

  const [loading, setLoading] = useState(false);

  const fetchChartData = useCallback(async () => {
    setLoading(true);

    try {
      const ids = {
        pId: selectedMerchant?.pId || null,
        qId: selectedMerchant?.qId || null,
        tId: selectedMerchant?.tId || null,
        kId: selectedMerchant?.kId || null,
      };

      let qbotDataRes = null;
      let paycentralDataRes = null;

      const apiCalls = [];

      if (tenantType === "all" || tenantType === "pid") {
        apiCalls.push(
          getPaycentralTransactionsChartDataAction({
            startDate,
            endDate,
            tenantId: ids.pId || "all",
            chartType: selectedTimeRange,
            websiteId: selectedWebsite || null,
          }).then(res => ({ type: "paycentral", res }))
        );
      }

      if (tenantType === "all" || tenantType === "qid") {
        apiCalls.push(
          getTransactionsChartDataAction({
            startDate,
            endDate,
            merchant: ids.qId,
            chartType: selectedTimeRange,
            websiteId: selectedWebsite || null,
          }).then(res => ({ type: "qbot", res }))
        );
      }

      // // 👉 FUTURE READY
      // if (tenantType === "all" || tenantType === "tid") {
      //   apiCalls.push(
      //     getTripolaTransactionsChartDataAction({
      //       startDate,
      //       endDate,
      //       tenantId: ids.tId,
      //       chartType: selectedTimeRange,
      //     }).then(res => ({ type: "tripola", res }))
      //   );
      // }

      // if (tenantType === "all" || tenantType === "kid") {
      //   apiCalls.push(
      //     getKycTransactionsChartDataAction({
      //       startDate,
      //       endDate,
      //       tenantId: ids.kId,
      //       chartType: selectedTimeRange,
      //     }).then(res => ({ type: "kyc", res }))
      //   );
      // }

      const results = await Promise.allSettled(apiCalls);

      const extracted = {
        paycentral: null,
        qbot: null,
        tripola: null,
        kyc: null,
      };

      results.forEach(r => {
        if (r.status === "fulfilled") {
          extracted[r.value.type] = r.value.res;
        }
      });

      const allLabels = [
        ...(extracted.qbot?.data?.labels || []),
        ...(extracted.paycentral?.data?.labels || []),
        ...(extracted.tripola?.data?.labels || []),
        ...(extracted.kyc?.data?.labels || []),
      ];

      const uniqueLabels = [...new Set(allLabels)].sort();

      const mapData = (labels, sourceLabels, sourceData) => {
        const map = {};
        sourceLabels.forEach((l, i) => {
          map[l] = sourceData[i];
        });
        return labels.map(l => map[l] || 0);
      };

      if (paycentralDataRes?.data?.warning) {
        onShowSnackbar(paycentralDataRes.data.warning, "warning");
      }

      const datasets = [];

      // QBOT
      if (extracted.qbot) {
        datasets.push({
          label: "Qbot",
          data: mapData(uniqueLabels,
            extracted.qbot?.data?.labels,
            extracted.qbot?.data?.data
          ),
          borderColor: "#3949AB",
          backgroundColor: "rgba(57,73,171,0.1)",
          tension: 0.4,
          fill: true,
        });
      }

      // PAYCENTRAL
      if (extracted.paycentral) {
        datasets.push({
          label: "PayCentral",
          data: mapData(uniqueLabels,
            extracted.paycentral?.data?.labels,
            extracted.paycentral?.data?.data
          ),
          borderColor: "#2E7D32",
          backgroundColor: "rgba(46,125,50,0.1)",
          tension: 0.4,
          fill: true,
        });
      }

      // TRIPOLA
      if (extracted.tripola) {
        datasets.push({
          label: "Tripola",
          data: mapData(uniqueLabels,
            extracted.tripola?.data?.labels,
            extracted.tripola?.data?.data
          ),
          borderColor: "#FB8C00",
          backgroundColor: "rgba(251,140,0,0.1)",
          tension: 0.4,
          fill: true,
        });
      }

      // KYC
      if (extracted.kyc) {
        datasets.push({
          label: "KYC",
          data: mapData(uniqueLabels,
            extracted.kyc?.data?.labels,
            extracted.kyc?.data?.data
          ),
          borderColor: "#00897B",
          backgroundColor: "rgba(0,137,123,0.1)",
          tension: 0.4,
          fill: true,
        });
      }


      setChartData({
        labels: uniqueLabels,
        datasets
      });

    } catch (error) {
      console.error("Chart fetch error:", error);

      setChartData({
        labels: [],
        datasets: []
      });

      onShowSnackbar("Failed to load transaction chart", "error");
    } finally {
      setLoading(false);
    }
  }, [
    startDate,
    endDate,
    selectedMerchant,
    selectedWebsite,
    selectedTimeRange,
    tenantType,
    onShowSnackbar
  ]);

  useEffect(() => {
    if (startDate && endDate && tenantType) {
      fetchChartData();
    }
  }, [startDate, endDate, selectedMerchant, selectedWebsite, tenantType, fetchChartData]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: { display: true, text: "Monthly Transactions" },
    },
    scales: {
      x: { title: { display: true, text: "Date / Month" } },
      y: {
        title: { display: true, text: "Number of Transactions" },
        beginAtZero: true,
        ticks: { stepSize: 1 },
      },
    },
  };

  return (
    <div className={`kpi-card ${!showButton && "my-3"}`}>
      {showButton && (
        <MyCancelIcon
          className="close-icon position-absolute cursor-pointer dash-close z-index-3"
          style={{ top: 5, right: 5 }}
          onClick={() => onRemove("totalTransactionCountChart")}
        />
      )}

      <div className="kpi-card-content">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h2 className="chart-title m-0">Total Transactions Count</h2>
        </div>

        <div className="chart-canvas-container" style={{ height: "350px" }}>
          {loading ? (
            <div className="chart-loading">
              <CircularProgress />
              <p>Loading transactions data...</p>
            </div>
          ) : !chartData.datasets.length ||
            chartData.datasets.every(ds => ds.data.every(v => v === 0)) ? (
            <NoDataFound message="No transaction data available" />
          ) : (
            <Line data={chartData} options={chartOptions} />
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardTransactionGraph;